import React, { useMemo } from 'react';
import StatusBadge from '../ui/StatusBadge';
import ExpandableSection from '../ui/ExpandableSection';
import Timeline from '../ui/Timeline';
import CopyButton from '../ui/CopyButton';
import KeyValueList from '../ui/KeyValueList';
import { formatDateTime } from '../../utils/formatters';
import { humanizePolicyId } from '../../utils/narration';
import { useDrawer } from '../ui/drawer/DrawerContext';

const formatDateTimeOrDash = (value) => formatDateTime(value) || '—';

const toTitleCase = (value) => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const humanizeProvider = (value) => toTitleCase(value);

const humanizeAction = (value) => {
  if (!value) return 'Request';
  if (value === 'data_export_csv') return 'Export data as CSV';
  return toTitleCase(value);
};

const humanizeResourceType = (value) => {
  if (!value) return 'Resource';
  const normalized = String(value).replace(/-/g, '_');
  const parts = normalized.split('_').filter(Boolean);
  if (parts.length > 1) {
    return toTitleCase(parts.slice(1).join(' '));
  }
  return toTitleCase(normalized);
};

const safeParseContext = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return { raw };
  }
};

const normalizeStatus = (value) => {
  const normalized = String(value || '').toUpperCase();
  if (normalized.includes('EXECUTED_SUCCESS') || normalized === 'SUCCEEDED') return 'Approved (executed)';
  if (normalized.includes('EXECUTED_FAILED') || normalized === 'FAILED') return 'Executed (failed)';
  if (normalized.includes('APPROVED')) return 'Approved';
  if (normalized.includes('DENIED')) return 'Denied';
  if (normalized.includes('EXPIRED')) return 'Expired';
  if (normalized.includes('CANCELLED')) return 'Cancelled';
  return 'Pending approvals';
};

const statusTone = (label) => {
  if (label === 'Approved (executed)' || label === 'Approved') return 'success';
  if (label === 'Executed (failed)' || label === 'Denied') return 'danger';
  if (label === 'Expired' || label === 'Cancelled') return 'muted';
  return 'default';
};

const CustomIntegrationModal = ({ release, policies = [] }) => {
  const { openDrawer } = useDrawer();
  if (!release) return null;
  const intent = release.intent || release;
  const provider = intent.provider || 'custom';
  const actionType = intent.action_type || intent.actionType || '';
  const resourceType = intent.resource_type || intent.resourceType || '';
  const resourceId = intent.resource_id || intent.resourceId || '';
  const environment = intent.environment || intent.env || '';
  const context = safeParseContext(intent.context_json || intent.context || intent.contextJson);
  const overallStatus = release.derived?.overallStatus || release.execution?.status || intent.status || '';
  const statusLabel = normalizeStatus(overallStatus);
  const opsConsoleUrl = intent.ops_console_url || intent.opsConsoleUrl || '';
  const policyId = release.policy?.id || intent.policy_id || intent.policyId || '';
  const policyLabel = release.policy?.display || humanizePolicyId(policyId, policies);
  const approvals = Array.isArray(release.approvals) ? release.approvals : [];
  const requiredRoles = release.policy?.requiredRoles
    || intent.required_roles
    || intent.requiredRoles
    || [];
  const approvalsRequired = requiredRoles.length || release.derived?.approvalsRequired || 0;

  const timelineItems = useMemo(() => {
    const items = [];
    const createdAt = intent.createdAt || intent.created_at || null;
    const requestedBy = context.requested_by || intent.requested_by || intent.requester || 'Requester';
    if (createdAt) {
      items.push({
        key: `created-${createdAt}`,
        title: `Request created by ${requestedBy}`,
        time: formatDateTimeOrDash(createdAt),
        timestamp: new Date(createdAt).getTime()
      });
    }
    if (approvalsRequired > 0 && approvals.length === 0) {
      items.push({
        key: 'approval-requested',
        title: `Approval requested (${approvalsRequired} required)`,
        time: createdAt ? formatDateTimeOrDash(createdAt) : '',
        timestamp: createdAt ? new Date(createdAt).getTime() : 0
      });
    }
    approvals.forEach((approval, index) => {
      const approverLabel = approval?.email || approval?.approverLabel || approval?.userId || approval?.wallet || 'Approver';
      const approvedAt = approval?.approvedAt || approval?.approved_at || null;
      items.push({
        key: `approved-${approval?.wallet || index}`,
        title: `Approval signed by ${approverLabel}`,
        time: formatDateTimeOrDash(approvedAt),
        timestamp: approvedAt ? new Date(approvedAt).getTime() : 0
      });
    });
    const executedAt = release.execution?.executedAt || release.execution?.executed_at || null;
    if (executedAt) {
      items.push({
        key: `executed-${executedAt}`,
        title: 'Execution recorded',
        time: formatDateTimeOrDash(executedAt),
        timestamp: new Date(executedAt).getTime()
      });
    }
    const evidenceAt = release.evidence?.recordedAt || release.evidence?.recorded_at || null;
    if (evidenceAt) {
      items.push({
        key: `evidence-${evidenceAt}`,
        title: 'Evidence recorded',
        time: formatDateTimeOrDash(evidenceAt),
        timestamp: new Date(evidenceAt).getTime()
      });
    }
    const filtered = items.filter((item) => item.title);
    const sorted = filtered.sort((a, b) => a.timestamp - b.timestamp);
    return sorted.length > 0 ? sorted : [{ key: 'none', title: 'No timeline events yet', time: '' }];
  }, [
    approvals,
    approvalsRequired,
    context.requested_by,
    intent.createdAt,
    intent.created_at,
    intent.requested_by,
    intent.requester,
    release.evidence?.recordedAt,
    release.evidence?.recorded_at,
    release.execution?.executedAt,
    release.execution?.executed_at
  ]);

  const contextEntries = [
    { label: 'Export format', value: context.export_format || '' },
    { label: 'Rows', value: context.rows != null ? String(context.rows) : '' },
    { label: 'Columns', value: Array.isArray(context.columns) ? context.columns.join(', ') : '' },
    { label: 'Destination', value: context.destination || '' },
    { label: 'Reason', value: context.reason || '' },
    { label: 'Filters', value: context.filters ? JSON.stringify(context.filters) : '' }
  ].filter((entry) => entry.value);

  const rawContext = context && Object.keys(context).length > 0
    ? JSON.stringify(context, null, 2)
    : '';

  return (
    <div className="release-details">
      <div className="release-header">
        <div>
          <h3>Request Details</h3>
          <div className="release-subtitle">
            <span>Integration: {humanizeProvider(provider)}</span>
          </div>
        </div>
        <div className="release-header__badges">
          <StatusBadge status={statusLabel} tone={statusTone(statusLabel)} />
          {environment && (
            <StatusBadge status={environment} tone="default" />
          )}
        </div>
      </div>

      <section className="release-section">
        <h4>Overview</h4>
        <KeyValueList
          items={[
            { label: 'Action', value: humanizeAction(actionType) },
            { label: 'Resource', value: `${humanizeResourceType(resourceType)}: ${resourceId || '—'}` },
            { label: 'Requested by', value: context.requested_by || intent.requested_by || intent.requester || 'Unknown' },
            { label: 'Created', value: formatDateTimeOrDash(intent.createdAt || intent.created_at) },
            { label: 'Policy', value: policyLabel || '—' },
            { label: 'Required approvals', value: approvalsRequired ? `${approvalsRequired} approvals` : '—' }
          ]}
        />
      </section>

      <section className="release-section">
        <h4>Timeline</h4>
        <Timeline items={timelineItems} />
      </section>

      <div className="release-actions">
        <CopyButton text={intent.id || ''} label="Copy Intent ID" />
        {opsConsoleUrl && (
          <a className="connect-button" href={opsConsoleUrl} target="_blank" rel="noreferrer">
            Open Ops Console
          </a>
        )}
        <button
          type="button"
          className="connect-button secondary"
          onClick={() => openDrawer('audit')}
        >
          View Audit Record
        </button>
      </div>

      <ExpandableSection title="Approvals">
        {approvals.length === 0 ? (
          <p className="muted">No approvals recorded yet.</p>
        ) : (
          <ul className="approval-list">
            {approvals.map((approval, index) => (
              <li key={approval.wallet || approval.userId || index}>
                <span className="approver">{approval.email || approval.approverLabel || approval.wallet || 'Approver'}</span>
                <span className="role">{approval.role || 'Signer'}</span>
                <span className="time">{formatDateTimeOrDash(approval.approvedAt || approval.approved_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </ExpandableSection>

      <ExpandableSection title="Execution">
        <KeyValueList
          items={[
            { label: 'Status', value: normalizeStatus(release.execution?.status || intent.execution_state || overallStatus) },
            { label: 'Recorded at', value: formatDateTimeOrDash(release.execution?.executedAt || release.execution?.executed_at) },
            { label: 'Execution hash', value: release.execution?.executedHash || release.execution?.executed_hash || '—' }
          ]}
        />
      </ExpandableSection>

      <ExpandableSection title="Evidence">
        <KeyValueList
          items={[
            { label: 'Chain tx', value: release.evidence?.tx || release.evidence?.tx_hash || release.execution?.onChainTxHash || '—' },
            { label: 'Evidence CID', value: release.evidence?.cid || release.evidence?.cid_hash || '—' }
          ]}
        />
        <button
          type="button"
          className="link-toggle"
          onClick={() => openDrawer('summary')}
        >
          View technical details
        </button>
      </ExpandableSection>

      <ExpandableSection title="Context">
        {contextEntries.length > 0 ? (
          <KeyValueList items={contextEntries} />
        ) : (
          <p className="muted">No structured context data provided.</p>
        )}
        <ExpandableSection title="Raw context (JSON)">
          {rawContext ? (
            <pre className="code-block">{rawContext}</pre>
          ) : (
            <p className="muted">No raw context available.</p>
          )}
          {rawContext && (
            <div className="panel-row">
              <CopyButton text={rawContext} label="Copy JSON" />
            </div>
          )}
        </ExpandableSection>
      </ExpandableSection>
    </div>
  );
};

export default CustomIntegrationModal;
